import { MenuItem } from '../MenuItem';
import { SelectOption } from './Select';
import { cn } from '../../lib/styles';

interface RadioMenuItemsProps<T> {
  options: SelectOption<T | null | undefined>[];
  selectedOption?: SelectOption<T | null | undefined> | null;
  children?: (children: React.ReactNode, option: SelectOption<T | null | undefined>) => React.ReactNode;
  onValueChange?: (value: T | null | undefined) => void;
}

interface RadioMenuItemProps<T> {
  option: SelectOption<T | null | undefined>;
  isSelected: boolean;
  wrapOption?: (children: React.ReactNode, option: SelectOption<T | null | undefined>) => React.ReactNode;
  onSelect: (value: T | null | undefined) => void;
}

export function RadioMenuItem<T>({ option, isSelected, onSelect, wrapOption: optionWrapper }: RadioMenuItemProps<T>) {
  const handleClick = () => {
    onSelect(option.value as T);
  };

  const radioIndicator = (
    <div
      className={cn(
        'aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-all outline-none',
        'flex items-center justify-center',
        isSelected ? 'border-text-primary bg-text-primary' : 'border-border-secondary bg-transparent',
        option.disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer group-hover/item:border-bg-inter-primary-active',
      )}
    >
      {isSelected && <div className="bg-white rounded-full size-2" />}
    </div>
  );

  const label = (
    <span
      className={cn(
        'font-semibold truncate block w-full text-left',
        option.disabled ? 'text-text-disabled cursor-not-allowed' : 'text-text-primary cursor-pointer',
      )}
    >
      {option.label}
    </span>
  );

  const menuItem = (
    <MenuItem
      className="w-full group/item flex items-center gap-2 justify-start"
      disabled={option.disabled}
      onClick={handleClick}
    >
      {radioIndicator}

      {label}
    </MenuItem>
  );

  if (!optionWrapper) return menuItem;

  return optionWrapper(menuItem, option);
}

export function RadioMenuItems<T>({ options, selectedOption, onValueChange, children }: RadioMenuItemsProps<T>) {
  const handleChange = (newValue: T | null | undefined) => {
    onValueChange?.(newValue);
  };

  return (
    <div className="gap-0 overflow-hidden">
      {options.map((option) => {
        const isSelected = selectedOption?.value === option.value;

        return (
          <RadioMenuItem
            key={String(option.value)}
            option={option}
            isSelected={isSelected}
            onSelect={handleChange}
            wrapOption={children}
          />
        );
      })}
    </div>
  );
}
